import { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { toDate } from '../../lib/dateValue'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  Plus, Scissors, GitMerge, Link2, Users, Star, Clock,
  Activity, CheckCircle, Shield, Archive, Calendar,
  GitBranch, ChevronRight, ExternalLink, Info, Layers,
  Box, MessageSquare, ArrowLeft, X, Play, Pause, RotateCcw,
  FileEdit,
} from 'lucide-react'
import { useItemsStore } from '../../stores/prioritization/itemsStore'
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore'
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore'
import { useSessionStore } from '../../stores/prioritization/sessionStore'
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore'
import { useAuditStore, type AuditEntry } from '../../stores/prioritization/auditStore'
import { getLineageForItem, type LineageEventRow } from '../../lib/lineageService'
import { auditToItemEvent, fieldChangeTitle, isCycleActivity, sessionIncludesItem, type ItemEventKind } from '../../components/prioritization/activity/itemEventMapping'
import { buildDescription, changeLines, changeValueText, namedPeopleIds } from '../../components/prioritization/item-view/activity/historyWords'
import { isUuid } from '../../lib/isUuid'
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap'
import { useVisualViewportFlags } from '../../hooks/prioritization/useVisualViewport'
import { relationshipGroupTimelineEntry } from '../../lib/relationshipGroupTimeline'
import { btnSecondary, FOCUS_RING, LABEL_MONO, TONE_FILL, type Tone } from '../../components/mono/primitives'
import { deriveStateSpans, durationInWords, isKeyMoment, journeyStory, rankOf, toneFor, type StateSpan } from './journey/journeyStory'

// ─── Types ────────────────────────────────────────────────────────────────────

export type JourneyEventType =
  | 'created'
  | 'shaping_started'
  | 'shaping_completed'
  | 'leadership_approved'
  | 'promoted'
  | 'archived'
  | 'deferred'
  | 'reactivated'
  | 'package_formed'
  | 'cluster_formed'
  | 'dependency_added'
  | 'aggregation_added'
  | 'elaboration_added'
  | 'session_added'
  | 'cycle_added'
  | 'lineage_split'
  | 'lineage_merge'
  // Item audit events (see components/prioritization/activity/itemEventMapping.ts)
  | 'item_changed'
  | 'imported'
  | 'completed'
  | 'retired'
  | 'classification_changed'
  | 'parent_changed'
  | 'dedupe_reviewed'
  | 'pool_added'
  | 'relationship_added'
  | 'relationship_dissolved'
  | 'decision_recorded'
  | 'lineage_fork'
  | 'lineage_supersede'
  | 'lineage_retire'

export type LaneId = 'lifecycle' | 'changes' | 'lineage' | 'cycles' | 'sessions' | 'relationships'

export interface JourneyEvent {
  id: string
  type: JourneyEventType
  lane: LaneId
  timestamp: Date
  label: string
  sublabel?: string
  detail?: string
  actor?: string
  linkedId?: string
  linkedLabel?: string
  linkedPath?: string
  isEstimated?: boolean
  /** Identity lane: another item joins this one's line ('in') or leaves it ('out'). */
  branch?: 'in' | 'out'
  /** The other item's title, or "3 items" when there are several. */
  branchLabel?: string
  /** A day's field edits drawn as one stop: each one, in words. */
  changes?: JourneyChange[]
}

/** One recorded change to the item, as the History log words it. */
export interface JourneyChange {
  id: string
  timestamp: Date
  label: string
  detail?: string
  actor?: string
}

export interface Lane {
  id: LaneId
  label: string
  textClass: string
  railHex: string
  bgClass: string
  gradientId: string
}

// ─── Lane config ──────────────────────────────────────────────────────────────

export const LANES: Lane[] = [
  {
    id: 'lifecycle',
    label: 'Lifecycle',
    textClass: 'text-[var(--text-sec)]',
    railHex: '#6366f1',
    bgClass: 'bg-[var(--surface)]/80',
    gradientId: 'rail-lifecycle',
  },
  {
    // Field edits (title, description, tags, types, values …): shown under Everything.
    id: 'changes',
    label: 'Changes',
    textClass: 'text-[var(--text-sec)]',
    railHex: '#6b7280',
    bgClass: 'bg-[var(--surface)]/80',
    gradientId: 'rail-changes',
  },
  {
    id: 'lineage',
    label: 'Identity',
    textClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    railHex: '#0d9488',
    bgClass: 'bg-[var(--green-dim)]/80',
    gradientId: 'rail-lineage',
  },
  {
    id: 'cycles',
    label: 'Cycles',
    textClass: 'text-[var(--coral)]',
    railHex: '#e11d48',
    bgClass: 'bg-[var(--coral-dim)]/80',
    gradientId: 'rail-cycles',
  },
  {
    id: 'sessions',
    label: 'Sessions & Results',
    textClass: 'text-[var(--text-ter)]',
    railHex: '#7c3aed',
    bgClass: 'bg-[var(--slate-dim)]/80',
    gradientId: 'rail-sessions',
  },
  {
    id: 'relationships',
    label: 'Relationships',
    textClass: 'text-[var(--mustard)]',
    railHex: '#ea580c',
    bgClass: 'bg-[var(--mustard-dim)]/80',
    gradientId: 'rail-relationships',
  },
]

// ─── Per-event-type visual config ─────────────────────────────────────────────

export const EVENT_CONFIG: Record<
  JourneyEventType,
  {
    icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
    hex: string
    glowHex: string
    ringHex: string
    badgeClass: string
    label: string
  }
> = {
  imported:            { icon: Plus,          hex: '#4f46e5', glowHex: '#a5b4fc', ringHex: '#e0e7ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Imported' },
  completed:           { icon: CheckCircle,   hex: '#047857', glowHex: '#6ee7b7', ringHex: '#d1fae5', badgeClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',  label: 'Completed' },
  retired:             { icon: Archive,       hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Retired' },
  classification_changed: { icon: Layers,     hex: '#0284c7', glowHex: '#7dd3fc', ringHex: '#e0f2fe', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Classification' },
  parent_changed:      { icon: GitBranch,     hex: '#0284c7', glowHex: '#7dd3fc', ringHex: '#e0f2fe', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Parent' },
  dedupe_reviewed:     { icon: Info,          hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Duplicate Review' },
  pool_added:          { icon: Box,           hex: '#b45309', glowHex: '#fcd34d', ringHex: '#fef3c7', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', label: 'Added to Pool' },
  item_changed:        { icon: FileEdit,      hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Changed' },
  relationship_added:  { icon: Link2,         hex: '#4f46e5', glowHex: '#a5b4fc', ringHex: '#e0e7ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Relationship' },
  relationship_dissolved: { icon: X,          hex: '#b91c1c', glowHex: '#fca5a5', ringHex: '#fee2e2', badgeClass: 'bg-[var(--coral-dim)] text-[var(--coral)]',  label: 'Dissolved' },
  decision_recorded:   { icon: Shield,        hex: '#b45309', glowHex: '#fcd34d', ringHex: '#fef3c7', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]', label: 'Decision' },
  lineage_fork:        { icon: GitBranch,     hex: '#0e7490', glowHex: '#67e8f9', ringHex: '#cffafe', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Forked' },
  lineage_supersede:   { icon: ChevronRight,  hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Superseded' },
  lineage_retire:      { icon: Archive,       hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]', label: 'Retired' },
  created:             { icon: Plus,          hex: '#4f46e5', glowHex: '#a5b4fc', ringHex: '#e0e7ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Created' },
  shaping_started:     { icon: Activity,      hex: '#0284c7', glowHex: '#7dd3fc', ringHex: '#e0f2fe', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',        label: 'Work Started' },
  shaping_completed:   { icon: CheckCircle,   hex: '#0369a1', glowHex: '#7dd3fc', ringHex: '#e0f2fe', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',        label: 'Item Ready' },
  leadership_approved: { icon: Shield,        hex: '#b45309', glowHex: '#fcd34d', ringHex: '#fef3c7', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',    label: 'Approved' },
  promoted:            { icon: Star,          hex: '#d97706', glowHex: '#fcd34d', ringHex: '#fef3c7', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',    label: 'Promoted' },
  archived:            { icon: Archive,       hex: '#6b7280', glowHex: '#d1d5db', ringHex: '#f3f4f6', badgeClass: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',      label: 'Archived' },
  deferred:            { icon: Clock,         hex: '#ca8a04', glowHex: '#fef08a', ringHex: '#fefce8', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',  label: 'Deferred' },
  reactivated:         { icon: RotateCcw,     hex: '#047857', glowHex: '#6ee7b7', ringHex: '#d1fae5', badgeClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',  label: 'Brought back to active' },
  package_formed:        { icon: Link2,         hex: '#ea580c', glowHex: '#fdba74', ringHex: '#ffedd5', badgeClass: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',  label: 'Package' },
  cluster_formed:      { icon: Layers,        hex: '#0891b2', glowHex: '#67e8f9', ringHex: '#ecfeff', badgeClass: 'bg-cyan-100 text-cyan-700',      label: 'Cluster' },
  dependency_added:    { icon: GitBranch,     hex: '#2563eb', glowHex: '#93c5fd', ringHex: '#eff6ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',      label: 'Dependency' },
  aggregation_added:   { icon: Box,           hex: '#059669', glowHex: '#6ee7b7', ringHex: '#ecfdf5', badgeClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]', label: 'Aggregation' },
  elaboration_added:   { icon: MessageSquare, hex: '#7c3aed', glowHex: '#c4b5fd', ringHex: '#f5f3ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Elaboration' },
  session_added:       { icon: Users,         hex: '#6d28d9', glowHex: '#c4b5fd', ringHex: '#f5f3ff', badgeClass: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',  label: 'Session' },
  cycle_added:         { icon: Calendar,      hex: '#be123c', glowHex: '#fda4af', ringHex: '#fff1f2', badgeClass: 'bg-[var(--coral-dim)] text-[var(--coral)]',      label: 'Cycle' },
  lineage_split:       { icon: Scissors,      hex: '#0f766e', glowHex: '#5eead4', ringHex: '#f0fdfa', badgeClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',      label: 'Split' },
  lineage_merge:       { icon: GitMerge,      hex: '#0d9488', glowHex: '#5eead4', ringHex: '#f0fdfa', badgeClass: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',      label: 'Merged' },
}

// ─── Lane assignment ──────────────────────────────────────────────────────────

export function laneFor(type: JourneyEventType): LaneId {
  switch (type) {
    case 'created':
    case 'shaping_started':
    case 'shaping_completed':
    case 'leadership_approved':
    case 'promoted':
    case 'archived':
    case 'deferred':
    case 'reactivated':
    case 'imported':
    case 'completed':
    case 'classification_changed':
    case 'parent_changed':
    case 'dedupe_reviewed':
    case 'decision_recorded':
      return 'lifecycle'
    case 'lineage_split':
    case 'lineage_merge':
    case 'lineage_fork':
    case 'lineage_supersede':
    case 'lineage_retire':
    case 'retired':
      return 'lineage'
    case 'cycle_added':
    case 'pool_added':
      return 'cycles'
    case 'session_added':
      return 'sessions'
    case 'package_formed':
    case 'cluster_formed':
    case 'dependency_added':
    case 'aggregation_added':
    case 'elaboration_added':
    case 'relationship_added':
    case 'relationship_dissolved':
      return 'relationships'
    case 'item_changed':
      return 'changes'
    default: {
      // Every event type needs a lane: one without a lane is counted under
      // Everything but drawn nowhere (field edits were, until 2026-10-16).
      const unplaced: never = type
      return unplaced
    }
  }
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export { toDate }

export function formatDate(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatDateShort(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function formatMonth(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

// ─── Event derivation ─────────────────────────────────────────────────────────

// ─── Audit-entry → lifecycle event mapping ────────────────────────────────────

type AuditEntryLike = AuditEntry

/**
 * Translate the shared audit vocabulary (activity/itemEventMapping.ts) into
 * this page's event union. The mapping itself is shared with the item lineage
 * timeline so the two screens can never drift apart again.
 */
const KIND_TO_JOURNEY: Record<ItemEventKind, JourneyEventType> = {
  created: 'created',
  imported: 'imported',
  shaping_started: 'shaping_started',
  shaping_completed: 'shaping_completed',
  leadership_approved: 'leadership_approved',
  promoted: 'promoted',
  archived: 'archived',
  deferred: 'deferred',
  reactivated: 'reactivated',
  completed: 'completed',
  retired: 'retired',
  classification_changed: 'classification_changed',
  parent_changed: 'parent_changed',
  dedupe_reviewed: 'dedupe_reviewed',
  pool_added: 'pool_added',
  cycle_added: 'cycle_added',
  relationship_added: 'relationship_added',
  relationship_dissolved: 'relationship_dissolved',
  decision_recorded: 'decision_recorded',
  lineage_split: 'lineage_split',
  lineage_fork: 'lineage_fork',
  item_changed: 'item_changed',
}

function auditToLifecycleType(et: string, toState?: string, fromState?: string): JourneyEventType | null {
  const kind = auditToItemEvent(et, toState, fromState)
  return kind ? KIND_TO_JOURNEY[kind] : null
}

export function deriveJourneyEvents(
  item: ReturnType<typeof useItemsStore>['items'][number],
  sessions: ReturnType<typeof useSessionStore>['sessions'],
  cycles: ReturnType<typeof useDegapProcessStore>['cycles'],
  groups: ReturnType<typeof useItemRelationshipsStore>['groups'],
  edges: ReturnType<typeof useItemRelationshipsStore>['edges'],
  allItems: ReturnType<typeof useItemsStore>['items'],
  auditEntries?: AuditEntryLike[],
  lineageRows?: LineageEventRow[],
  /** Cycle-bound item pools — how items actually reach a cycle today. */
  pools?: { id: string; name?: string; cycleId?: string; priorityIds?: string[]; createdAt?: Date | string }[],
  /** User id → name, for a change that names a person (owner, delegate). */
  names?: Record<string, string>,
): JourneyEvent[] {
  const raw: Omit<JourneyEvent, 'lane'>[] = []
  const createdAt = toDate(item.createdAt)

  // ── Lifecycle events: audit rows first, then whatever the item's own fields
  // know that the audit trail didn't record. These used to be either/or, so a
  // single audit row disabled field derivation entirely.
  const typesFromAudit = new Set<JourneyEventType>()
  const sessionNames: Record<string, string> = Object.fromEntries(sessions.map((s: any) => [s.id, s.name ?? 'Session']))
  const itemById = (id: string) => allItems.find((i: any) => i.id === id) ?? null
  if (auditEntries && auditEntries.length > 0) {
    for (const entry of auditEntries) {
      // The cycle's own record (cut line moved, ball claimed) is not this item's journey.
      if (isCycleActivity(entry)) continue
      const ts = toDate(entry.timestamp)
      if (!ts) continue
      const journeyType = auditToLifecycleType(entry.eventType, entry.toState, entry.fromState)
      if (!journeyType) continue

      const cfg = EVENT_CONFIG[journeyType]
      typesFromAudit.add(journeyType)
      if (journeyType === 'item_changed' || journeyType === 'relationship_added' || journeyType === 'relationship_dissolved') {
        // Any other change, or a tie to another item: the History log's own words for
        // it ("Requires: “Pen-test findings”"), and what it changed.
        const lines = changeLines(entry, itemById, names)
        raw.push({
          id: `audit-${entry.id}`,
          type: journeyType,
          timestamp: ts,
          label: buildDescription(entry, sessionNames, itemById, item.itemCategory),
          actor: entry.userId,
          detail: lines.length > 0
            ? lines.map(changeValueText).join(' · ')
            : typeof entry.payload?.rationale === 'string' ? entry.payload.rationale : undefined,
        })
        continue
      }
      // A field edit is titled by what changed, not by its kind ("Classification").
      const change = fieldChangeTitle(entry.changeSummary)
      raw.push({
        id: `audit-${entry.id}`,
        type: journeyType,
        timestamp: ts,
        label: change?.label ?? cfg.label,
        actor: entry.userId,
        detail: entry.note ?? change?.detail ?? (entry.actorName ? `By ${entry.actorName}` : undefined),
      })
    }
  }
  {
    // Field derivation, skipped per type when the audit trail already has it.
    const pushIfNew = (e: Omit<JourneyEvent, 'lane'>) => {
      if (typesFromAudit.has(e.type)) return
      raw.push(e)
    }
    if (createdAt) {
      pushIfNew({ id: 'created', type: 'created', timestamp: createdAt, label: 'Item Created',
        detail: item.createdBy ? `By ${item.createdBy}` : undefined, actor: item.createdBy as string | undefined })
    }

    const shapingStartedAt = toDate((item as any).shapingStartedAt)
    if (shapingStartedAt) pushIfNew({ id: 'shaping-started', type: 'shaping_started', timestamp: shapingStartedAt, label: 'Work Started', actor: (item as any).shapingOwnerId })

    const shapedAt = toDate((item as any).shapedAt)
    if (shapedAt) pushIfNew({ id: 'shaping-completed', type: 'shaping_completed', timestamp: shapedAt, label: 'Item Ready' })

    const leadershipApprovedAt = toDate((item as any).leadershipApprovedAt)
    if (leadershipApprovedAt) pushIfNew({ id: 'leadership-approved', type: 'leadership_approved', timestamp: leadershipApprovedAt, label: 'Leadership Approved', actor: (item as any).leadershipApprovedBy })

    const promotedAt = toDate((item as any).promotedAt)
    if (promotedAt) {
      pushIfNew({ id: 'promoted', type: 'promoted', timestamp: promotedAt, label: 'Promoted to Priority',
        detail: (item as any).promotionCohortId ? `Cohort · ${String((item as any).promotionCohortId).slice(0, 8)}` : undefined,
        actor: (item as any).promotedBy, linkedId: (item as any).sourceSessionId })
    }

    const exitStatus = (item as any).exit_status as string | undefined
    const updatedAt = toDate(item.updatedAt)
    if (exitStatus === 'deferred' && updatedAt && updatedAt !== createdAt)
      pushIfNew({ id: 'deferred', type: 'deferred', timestamp: updatedAt, label: 'Deferred', isEstimated: true })
    if (exitStatus === 'archived' && updatedAt && updatedAt !== createdAt)
      pushIfNew({ id: 'archived', type: 'archived', timestamp: updatedAt, label: 'Archived', isEstimated: true })
  }

  // ── Identity/lineage events: lineage_events DB table is authoritative ──────
  if (lineageRows && lineageRows.length > 0) {
    for (const row of lineageRows) {
      const ts = toDate(row.created_at)
      if (!ts) continue
      const isSource = row.source_item_ids.includes(item.id)
      const isTarget = row.target_item_ids.includes(item.id)

      if (row.event_type === 'merge' && isTarget) {
        // This item is the result of merging other items into it
        const sourceItems = row.source_item_ids.map((id) => allItems.find((i: any) => i.id === id)?.title ?? id.slice(0, 8))
        raw.push({ id: `lineage-${row.id}`, type: 'lineage_merge', timestamp: ts,
          label: `Merged (${row.source_item_ids.length} item${row.source_item_ids.length !== 1 ? 's' : ''})`,
          detail: sourceItems.join(', '), actor: row.performed_by ?? undefined,
          branch: 'in',
          branchLabel: row.source_item_ids.length === 1 ? sourceItems[0] : `${row.source_item_ids.length} items`,
          linkedId: row.source_item_ids[0],
          linkedLabel: allItems.find((i: any) => i.id === row.source_item_ids[0])?.title ?? 'View source',
          linkedPath: row.source_item_ids[0] ? `/prioritization/items/${row.source_item_ids[0]}` : undefined })
      } else if (row.event_type === 'split' && isTarget) {
        // This item was created by splitting another item
        const parentId = row.source_item_ids[0]
        const parent = allItems.find((i: any) => i.id === parentId)
        raw.push({ id: `lineage-${row.id}`, type: 'lineage_split', timestamp: ts,
          label: 'Split from Item', detail: parent?.title ?? row.rationale ?? undefined,
          actor: row.performed_by ?? undefined,
          branch: 'in', branchLabel: parent?.title ?? 'another item',
          linkedId: parentId, linkedLabel: parent?.title ?? 'View parent',
          linkedPath: parentId ? `/prioritization/items/${parentId}` : undefined })
      } else if (row.event_type === 'fork' && isTarget) {
        const parentId = row.source_item_ids[0]
        const parent = allItems.find((i: any) => i.id === parentId)
        raw.push({ id: `lineage-${row.id}`, type: 'lineage_fork', timestamp: ts,
          label: 'Forked from Item', detail: parent?.title ?? undefined,
          actor: row.performed_by ?? undefined,
          branch: 'in', branchLabel: parent?.title ?? 'another item',
          linkedId: parentId, linkedLabel: parent?.title ?? 'View parent',
          linkedPath: parentId ? `/prioritization/items/${parentId}` : undefined })
      } else if (row.event_type === 'supersede' && isSource) {
        // This item was superseded (replaced by target)
        const successor = row.target_item_ids[0]
        const successorItem = allItems.find((i: any) => i.id === successor)
        raw.push({ id: `lineage-${row.id}`, type: 'lineage_supersede', timestamp: ts,
          label: 'Superseded', detail: successorItem?.title ?? row.rationale ?? undefined,
          actor: row.performed_by ?? undefined,
          branch: 'out', branchLabel: successorItem?.title ?? 'its successor',
          linkedId: successor, linkedLabel: successorItem?.title ?? 'View successor',
          linkedPath: successor ? `/prioritization/items/${successor}` : undefined })
      } else if (row.event_type === 'retire' && isSource) {
        raw.push({ id: `lineage-${row.id}`, type: 'lineage_retire', timestamp: ts,
          label: 'Retired', detail: row.rationale ?? undefined, actor: row.performed_by ?? undefined })
      } else if (row.event_type === 'split' && isSource) {
        // This item was split into pieces
        raw.push({ id: `lineage-${row.id}-src`, type: 'lineage_split', timestamp: ts,
          label: `Split into ${row.target_item_ids.length} items`,
          detail: row.rationale ?? undefined, actor: row.performed_by ?? undefined,
          branch: 'out',
          branchLabel: row.target_item_ids.length === 1
            ? (allItems.find((i: any) => i.id === row.target_item_ids[0])?.title ?? '1 item')
            : `${row.target_item_ids.length} items` })
      }
    }
  } else {
    // Fallback: derive split/merge from item fields when no lineage rows are available.
    const splitFromId = (item as any).splitFromId
    if (splitFromId) {
      const parent = allItems.find((i: any) => i.id === splitFromId)
      raw.push({ id: 'lineage-split', type: 'lineage_split', timestamp: createdAt ?? new Date(),
        label: 'Split from Item', detail: parent?.title ?? splitFromId,
        branch: 'in', branchLabel: parent?.title ?? 'another item',
        linkedId: splitFromId, linkedLabel: parent?.title ?? 'View parent',
        linkedPath: `/prioritization/items/${splitFromId}`, isEstimated: !createdAt })
    }

    const mergedFromIds = (item as any).mergedFromIds as string[] | undefined
    if (mergedFromIds?.length) {
      raw.push({ id: 'lineage-merge', type: 'lineage_merge', timestamp: createdAt ?? new Date(),
        label: `Merged (${mergedFromIds.length} items)`,
        detail: mergedFromIds.map((id: string) => allItems.find((i: any) => i.id === id)?.title ?? id.slice(0, 8)).join(', '),
        isEstimated: !createdAt })
    }
  }

  cycles.filter((c: any) => c.itemIds?.includes(item.id)).forEach((cycle: any) => {
    const ts = toDate(cycle.createdAt); if (!ts) return
    raw.push({ id: `cycle-${cycle.id}`, type: 'cycle_added', timestamp: ts,
      label: cycle.name ?? 'Prioritization Cycle', detail: cycle.phase ? `Phase: ${cycle.phase}` : undefined,
      linkedId: cycle.id, linkedLabel: cycle.name ?? 'View cycle', linkedPath: `/prioritization/cycles/${cycle.id}` })
  })

  // Cycles reached through a pool. `cycle_item_participation` is the canonical
  // record (canon 03), but nothing has written it since 2026-07-22: items now
  // enter a cycle through a cycle-bound item pool, a path that records the
  // comparison set and an audit event but no participation row. Without this,
  // every item in every cycle created since then shows an empty Cycles lane.
  const cycleIdsAlreadyShown = new Set(raw.filter((e) => e.type === 'cycle_added').map((e) => e.linkedId))
  ;(pools ?? []).forEach((pool) => {
    if (!pool.cycleId || !pool.priorityIds?.includes(item.id)) return
    if (cycleIdsAlreadyShown.has(pool.cycleId)) return
    cycleIdsAlreadyShown.add(pool.cycleId)
    const cycle = cycles.find((c: any) => c.id === pool.cycleId)
    const ts = toDate(pool.createdAt) ?? createdAt
    if (!ts) return
    raw.push({
      id: `cycle-via-pool-${pool.cycleId}-${pool.id}`,
      type: 'cycle_added',
      timestamp: ts,
      label: (cycle as any)?.name ?? 'Cycle',
      sublabel: pool.name ? `via ${pool.name}` : 'via item pool',
      // The pool records no per-item timestamp, so this is the pool's date.
      isEstimated: true,
      linkedId: pool.cycleId,
      linkedLabel: (cycle as any)?.name ?? 'View cycle',
      linkedPath: `/prioritization/cycles/${pool.cycleId}`,
    })
  })

  sessions.filter((s: any) => sessionIncludesItem(s, item.id)).forEach((session: any) => {
    const ts = toDate(session.createdAt); if (!ts) return
    let rankDetail: string | undefined
    const ro = session.rankingOutput as { rankedItemIds?: string[]; orderedItems?: { id: string }[]; scores?: Record<string, Record<string, number> & { total?: number }> } | undefined
    if (ro) {
      const orderedIds = ro.rankedItemIds ?? ro.orderedItems?.map((i) => i.id) ?? []
      const rankIndex = orderedIds.indexOf(item.id)
      const rank = rankIndex >= 0 ? rankIndex + 1 : undefined
      const score = ro.scores?.[item.id]?.total
      const parts: string[] = []
      if (rank != null) parts.push(`#${rank} of ${orderedIds.length}`)
      if (score != null) parts.push(`${score.toFixed(1)}pts`)
      if (parts.length) rankDetail = parts.join(' · ')
    }
    raw.push({ id: `session-${session.id}`, type: 'session_added', timestamp: ts,
      label: session.name ?? 'Ranking Session', sublabel: rankDetail,
      detail: session.state ? `State: ${session.state}` : undefined,
      linkedId: session.id, linkedLabel: session.name ?? 'View session',
      linkedPath: `/prioritization/sessions/${session.id}` })
  })

  groups.forEach((group: any) => {
    const ts = toDate(group.createdAt); if (!ts) return
    const entry = relationshipGroupTimelineEntry(group)
    raw.push({ id: `group-${group.id}`, type: entry.eventType, timestamp: ts,
      label: entry.label,
      detail: group.rationale, linkedId: group.id,
      linkedLabel: entry.linkedLabel,
      linkedPath: entry.linkedPath })
  })

  edges.filter((e: any) => e.sourceItemId === item.id || e.targetItemId === item.id).forEach((edge: any) => {
    const ts = toDate(edge.createdAt); if (!ts) return
    const isSource = edge.sourceItemId === item.id
    const otherId = isSource ? edge.targetItemId : edge.sourceItemId
    const other = allItems.find((i: any) => i.id === otherId)
    const edgeEventType: JourneyEventType =
      edge.type === 'dependency' ? 'dependency_added' :
      edge.type === 'aggregation' ? 'aggregation_added' :
      edge.type === 'elaboration' ? 'elaboration_added' : 'dependency_added'
    const edgeLabel =
      edge.type === 'dependency' ? (isSource ? '→ Depends On' : '← Required By') :
      edge.type === 'aggregation' ? (isSource ? '→ Aggregates' : '← Aggregated by') :
      edge.type === 'elaboration' ? (isSource ? '→ Elaborates' : '← Elaborated by') : 'Related'
    raw.push({ id: `edge-${edge.id}`, type: edgeEventType, timestamp: ts, label: edgeLabel,
      sublabel: edge.subtype ?? undefined, detail: other?.title ?? otherId?.slice(0, 8),
      linkedId: otherId, linkedLabel: other?.title ?? 'View item',
      linkedPath: other ? `/prioritization/items/${otherId}` :
        edge.type === 'dependency' ? '/prioritization/relationships/dependencies' :
        edge.type === 'elaboration' ? '/prioritization/relationships/elaborations' :
        '/prioritization/relationships/aggregations' })
  })

  const seen = new Set<string>()
  return raw
    .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
    .map((e) => ({ ...e, lane: laneFor(e.type) }))
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

/**
 * A day of edits is one stop. Thirty edits on one day used to be thirty stops,
 * pushed apart until they ran off the right edge, each labelled "Changed".
 * Now the stop says how many, and opening it lists each one in words.
 */
export function groupChangesByDay(events: JourneyEvent[]): JourneyEvent[] {
  const byDay = new Map<string, JourneyEvent[]>()
  const out: JourneyEvent[] = []
  for (const e of events) {
    if (e.type !== 'item_changed') { out.push(e); continue }
    const t = e.timestamp
    const day = `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`
    const list = byDay.get(day)
    if (list) list.push(e)
    else byDay.set(day, [e])
  }
  for (const [day, list] of byDay) {
    const changes: JourneyChange[] = list.map((e) => ({ id: e.id, timestamp: e.timestamp, label: e.label, detail: e.detail, actor: e.actor }))
    const actors = new Set(list.map((e) => e.actor ?? ''))
    out.push({
      id: list.length === 1 ? list[0].id : `changes-${day}`,
      type: 'item_changed',
      lane: 'changes',
      timestamp: list[0].timestamp,
      label: list.length === 1 ? list[0].label : `${list.length} changes`,
      sublabel: list.length === 1 ? undefined : `${list[0].label}${list.length > 1 ? ` and ${list.length - 1} more` : ''}`,
      detail: list.length === 1 ? list[0].detail : undefined,
      actor: actors.size === 1 ? list[0].actor : undefined,
      changes: list.length === 1 ? undefined : changes,
    })
  }
  return out.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
}

// ─── Layout constants ─────────────────────────────────────────────────────────

export const RULER_HEIGHT = 44          // time ruler strip height
const LANE_LABEL_W = 148                // fixed left label column
const STOP_PX = 28                      // event stop diameter
const STOP_GAP_PX = 32                  // closest two stops on one lane may sit
const LABEL_W_PX = 128                  // width of the label under a stop
const BAND_ROW_H = 64                   // the state band's row
const BRANCH_REACH = 46                 // how far a branch runs before it joins the line
const DAY_MS = 86400000

// ─── Tone → colour (tokens only; each is defined for Paper and Night) ─────────

const TONE_COLOR: Record<Tone, string> = {
  slate: 'var(--slate)',
  mustard: 'var(--mustard)',
  green: 'var(--green)',
  coral: 'var(--coral)',
  neutral: 'var(--text-dim)',
}

/** The glyph inside a filled stop. Gold and neutral fills are light in Night, so they take the ground colour. */
const TONE_INK: Record<Tone, string> = {
  slate: 'white',
  green: 'white',
  coral: 'white',
  mustard: 'var(--bg-raised)',
  neutral: 'var(--bg-raised)',
}

// ─── Time / layout helpers ────────────────────────────────────────────────────

function buildTimeScale(tMin: number, tMax: number, canvasWidth: number, padLeft: number, padRight: number) {
  const span = tMax - tMin || 1
  const usable = canvasWidth - padLeft - padRight
  return (t: number) => padLeft + ((t - tMin) / span) * usable
}

export function buildRulerTicks(tMin: number, tMax: number, toX: (t: number) => number) {
  const ticks: { x: number; label: string }[] = []
  const span = tMax - tMin; if (span <= 0) return ticks
  const DAY = 86400000
  let interval = DAY
  if (span > 365 * DAY * 2) interval = 91 * DAY
  else if (span > 365 * DAY) interval = 30 * DAY
  else if (span > 60 * DAY) interval = 14 * DAY
  else if (span > 14 * DAY) interval = 7 * DAY
  const start = new Date(tMin); start.setHours(0, 0, 0, 0)
  let t = start.getTime()
  while (t <= tMax + interval) {
    const d = new Date(t)
    ticks.push({ x: toX(t), label: interval >= 30 * DAY ? formatMonth(d) : formatDateShort(d) })
    t += interval
  }
  return ticks
}

interface StopLayout {
  event: JourneyEvent
  x: number
  /** Which row of labels under the line this stop's label sits on. */
  level: number
}

interface LaneLayout {
  lane: Lane
  top: number
  height: number
  railY: number
  levelHeight: number
  stops: StopLayout[]
}

/**
 * Place each lane's stops. A stop sits on its date; when two fall closer than a
 * stop's width the later one moves just clear of the earlier, and labels that
 * would overlap drop to the next row down instead of pushing stops apart.
 */
function layoutLanes(events: JourneyEvent[], lanes: Lane[], toX: (t: number) => number, top0: number): LaneLayout[] {
  const layouts: LaneLayout[] = []
  let top = top0
  for (const lane of lanes) {
    const laneEvents = events.filter((e) => e.lane === lane.id)
    const levelEnds: number[] = []
    let prevX = -Infinity
    const stops: StopLayout[] = laneEvents.map((event) => {
      const x = Math.max(toX(event.timestamp.getTime()), prevX + STOP_GAP_PX)
      prevX = x
      const left = x - LABEL_W_PX / 2
      let level = levelEnds.findIndex((end) => left >= end + 8)
      if (level === -1) {
        if (levelEnds.length < 3) level = levelEnds.length
        else level = levelEnds.indexOf(Math.min(...levelEnds))
      }
      levelEnds[level] = left + LABEL_W_PX
      return { event, x, level }
    })
    const hasBranch = laneEvents.some((e) => e.branch)
    const levelHeight = laneEvents.some((e) => e.sublabel) ? 60 : 44
    const above = hasBranch ? 62 : 36
    const height = above + STOP_PX / 2 + 8 + Math.max(levelEnds.length, 1) * levelHeight + 10
    layouts.push({ lane, top, height, railY: top + above, levelHeight, stops })
    top += height
  }
  return layouts
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

export function DetailPanel({ event, actorDisplayMap, onClose }: {
  event: JourneyEvent; actorDisplayMap: Record<string, string>; onClose: () => void
}) {
  const cfg = EVENT_CONFIG[event.type]
  const Icon = cfg.icon
  const lane = LANES.find((l) => l.id === event.lane)
  const tone = toneFor(event.type)
  const rank = rankOf(event)
  return (
    <div className="h-full flex flex-col bg-[var(--bg-raised)]">
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--rule)' }}>
        <span className={LABEL_MONO}>{lane?.label}</span>
        <button onClick={onClose} aria-label="Close"
          className={`p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] transition-colors ${FOCUS_RING}`}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="px-4 pt-4 pb-3 flex items-start gap-3">
        <span className="flex-shrink-0 flex items-center justify-center rounded-full"
          style={{ width: 36, height: 36, background: TONE_COLOR[tone] }}>
          <Icon style={{ width: 16, height: 16, color: TONE_INK[tone] }} />
        </span>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-base leading-tight" style={{ color: 'var(--text-pri)' }}>{event.label}</div>
          {rank ? (
            <div className="mt-1 font-mono text-lg text-[var(--mustard)]">{rank}</div>
          ) : event.sublabel && !event.changes ? (
            <div className="mt-1 text-sm text-[var(--text-sec)]">{event.sublabel}</div>
          ) : null}
        </div>
      </div>
      <div className="px-4 space-y-4 flex-1 overflow-y-auto pb-4">
        <div>
          <div className={`${LABEL_MONO} mb-1`}>Date</div>
          <div className="text-sm text-[var(--text-sec)]">
            {formatDate(event.timestamp)}
            {event.isEstimated && <span className="text-[var(--text-dim)]"> · date estimated</span>}
          </div>
        </div>
        {rank && event.sublabel && event.sublabel !== rank && (
          <div>
            <div className={`${LABEL_MONO} mb-1`}>Result</div>
            <div className="text-sm text-[var(--text-sec)]">{event.sublabel}</div>
          </div>
        )}
        {event.actor && (
          <div>
            <div className={`${LABEL_MONO} mb-1`}>By</div>
            <div className="text-sm text-[var(--text-sec)]">{actorDisplayMap[event.actor] ?? 'Someone'}</div>
          </div>
        )}
        {event.detail && (
          <div>
            <div className={`${LABEL_MONO} mb-1`}>{event.type === 'item_changed' ? 'What changed' : 'Detail'}</div>
            <div className="text-sm text-[var(--text-sec)]">{event.detail}</div>
          </div>
        )}
        {event.changes && (
          <ol className="border-t" style={{ borderColor: 'var(--rule)' }}>
            {event.changes.map((change) => (
              <li key={change.id} className="py-2.5 border-b" style={{ borderColor: 'var(--rule)' }}>
                <div className="text-sm font-medium text-[var(--text-pri)]">{change.label}</div>
                {change.detail && <div className="mt-0.5 text-sm text-[var(--text-sec)] break-words">{change.detail}</div>}
                <div className="mt-0.5 text-xs text-[var(--text-dim)]">
                  {!event.actor && change.actor ? `${actorDisplayMap[change.actor] ?? 'Someone'} · ` : ''}
                  {change.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              </li>
            ))}
          </ol>
        )}
        {event.linkedLabel && event.linkedPath && (
          <div className="pt-1">
            <Link to={event.linkedPath} {...btnSecondary('max-w-full')}>
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" /><span className="truncate">{event.linkedLabel}</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Phone / tablet list row ──────────────────────────────────────────────────

function MobileEventRow({ event, onSelect, isSelected }: {
  event: JourneyEvent; onSelect: (id: string | null) => void; isSelected: boolean
}) {
  const cfg = EVENT_CONFIG[event.type]
  const Icon = cfg.icon
  const tone = toneFor(event.type)
  const rank = rankOf(event)
  return (
    <button
      className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b transition-colors hover:bg-[var(--hover-tint)] ${isSelected ? 'bg-[var(--active-tint)]' : ''} ${FOCUS_RING}`}
      style={{ borderColor: 'var(--rule)' }}
      onClick={() => onSelect(isSelected ? null : event.id)}>
      <span className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
        style={event.isEstimated
          ? { width: STOP_PX, height: STOP_PX, border: `2px solid ${TONE_COLOR[tone]}` }
          : { width: STOP_PX, height: STOP_PX, background: TONE_COLOR[tone] }}>
        <Icon style={{ width: 13, height: 13, color: event.isEstimated ? TONE_COLOR[tone] : TONE_INK[tone] }} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-[var(--text-pri)]">{event.label}</span>
        {rank && <span className="block font-mono text-sm text-[var(--mustard)]">{rank}</span>}
        {!rank && event.sublabel && <span className="block text-xs text-[var(--text-sec)]">{event.sublabel}</span>}
        <span className="block font-mono text-xs text-[var(--text-dim)] mt-0.5">{formatDate(event.timestamp)}</span>
        {event.detail && <span className="block text-xs text-[var(--text-sec)] mt-0.5 truncate">{event.detail}</span>}
      </span>
    </button>
  )
}

// ─── The state band ───────────────────────────────────────────────────────────

function StateBand({ spans, toX, top }: { spans: StateSpan[]; toX: (t: number) => number; top: number }) {
  return (
    <>
      {spans.map((span, i) => {
        const left = toX(span.start.getTime())
        const width = Math.max(toX(span.end.getTime()) - left - 2, 3)
        const stay = durationInWords(span.end.getTime() - span.start.getTime())
        const title = span.ended ? `${span.label} ${formatDate(span.start)}` : `${span.label} · ${stay}`
        return (
          <div key={i} title={title}
            className={`absolute flex items-center gap-2 overflow-hidden whitespace-nowrap px-2.5 ${TONE_FILL[span.tone]}`}
            style={span.ended ? { left, top, height: 32 } : { left, top, height: 32, width }}>
            {(span.ended || width >= 72) && (
              <span className="font-mono text-xs uppercase tracking-[0.08em]">{span.label}</span>
            )}
            {!span.ended && width >= 150 && <span className="text-xs text-[var(--text-dim)]">{stay}</span>}
          </div>
        )
      })}
    </>
  )
}

// ─── The map ──────────────────────────────────────────────────────────────────

interface JourneyMapProps {
  /** The events to draw as stops. */
  events: JourneyEvent[]
  /** Every event, shown or not — the time axis and the state band use all of them. */
  allEvents: JourneyEvent[]
  activeLanes: Lane[]
  now: Date
  selectedId: string | null
  onSelect: (id: string | null) => void
  playheadProgress: number
  zoom: number
}

export function JourneyMap({ events, allEvents, activeLanes, now, selectedId, onSelect, playheadProgress, zoom }: JourneyMapProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [viewportW, setViewportW] = useState(960)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setViewportW(el.clientWidth))
    ro.observe(el)
    setViewportW(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  // At 100% the whole journey fits the window; zooming in is what makes it scroll.
  const canvasWidth = Math.max(viewportW - LANE_LABEL_W, 640) * zoom
  // A branch needs room to name the item it comes from or goes to.
  const padLeft = events.some((e) => e.branch === 'in') ? 210 : 72
  const padRight = events.some((e) => e.branch === 'out') ? 220 : 104

  const tMin = allEvents[0].timestamp.getTime()
  const tMax = Math.max(now.getTime(), allEvents[allEvents.length - 1].timestamp.getTime(), tMin + DAY_MS)

  const toX = useMemo(() => buildTimeScale(tMin, tMax, canvasWidth, padLeft, padRight), [tMin, tMax, canvasWidth, padLeft, padRight])
  const spans = useMemo(() => deriveStateSpans(allEvents, now), [allEvents, now])
  const laneLayouts = useMemo(
    () => layoutLanes(events, activeLanes, toX, RULER_HEIGHT + BAND_ROW_H),
    [events, activeLanes, toX],
  )
  const todayX = toX(now.getTime())
  const rulerTicks = useMemo(
    () => buildRulerTicks(tMin, tMax, toX).filter((tick) => tick.x >= padLeft - 8 && tick.x < todayX - 44),
    [tMin, tMax, toX, padLeft, todayX],
  )
  const lastLane = laneLayouts[laneLayouts.length - 1]
  const totalHeight = lastLane ? lastLane.top + lastLane.height : RULER_HEIGHT + BAND_ROW_H
  // Stops too close together are moved apart; if that pushes one past the edge,
  // the map widens to hold it (and scrolls) rather than cutting it off.
  const lastStopRight = Math.max(0, ...laneLayouts.flatMap((l) => l.stops.map((st) => st.x + LABEL_W_PX / 2 + 16)))
  const mapWidth = Math.max(canvasWidth, lastStopRight)
  const playing = playheadProgress > 0 && playheadProgress < 1
  const playheadT = tMin + playheadProgress * (tMax - tMin)
  const playheadX = toX(playheadT)
  // The item's line stops where its identity ended.
  const ending = allEvents.find((e) => e.type === 'lineage_retire' || e.type === 'lineage_supersede' || e.type === 'retired')
  const lineEndX = ending ? toX(ending.timestamp.getTime()) : todayX

  return (
    <div ref={scrollRef} className="overflow-x-auto">
      <div className="flex" style={{ width: LANE_LABEL_W + mapWidth }}>
        {/* ── Lane names — stay put while the map scrolls ── */}
        <div className="sticky left-0 z-10 flex-shrink-0 bg-[var(--bg-raised)] border-r"
          style={{ width: LANE_LABEL_W, height: totalHeight, borderColor: 'var(--rule)' }}>
          <div style={{ height: RULER_HEIGHT }} />
          <div className={`${LABEL_MONO} flex items-start px-4 pt-[22px] border-t`}
            style={{ height: BAND_ROW_H, borderColor: 'var(--rule)' }}>State</div>
          {laneLayouts.map(({ lane, height, railY, top }) => (
            <div key={lane.id} className={`${LABEL_MONO} px-4 border-t`}
              style={{ height, paddingTop: railY - top - 9, borderColor: 'var(--rule)' }}>
              {lane.label}
            </div>
          ))}
        </div>

        {/* ── Canvas ── */}
        <div className="relative select-none" style={{ width: mapWidth, height: totalHeight }}>
          <svg width={mapWidth} height={totalHeight} className="absolute inset-0 block" aria-hidden="true">
            {/* Ruler */}
            {rulerTicks.map((tick, i) => (
              <g key={i}>
                <line x1={tick.x} y1={RULER_HEIGHT - 6} x2={tick.x} y2={RULER_HEIGHT} style={{ stroke: 'var(--rule-bright)' }} />
                <text x={tick.x} y={RULER_HEIGHT - 12} textAnchor="middle" className="font-mono" fontSize={12}
                  style={{ fill: 'var(--text-dim)' }}>{tick.label}</text>
              </g>
            ))}
            {/* Row rules */}
            <line x1={0} y1={RULER_HEIGHT} x2={mapWidth} y2={RULER_HEIGHT} style={{ stroke: 'var(--rule)' }} />
            {laneLayouts.map(({ lane, top }) => (
              <line key={lane.id} x1={0} y1={top} x2={mapWidth} y2={top} style={{ stroke: 'var(--rule)' }} />
            ))}

            {laneLayouts.map(({ lane, railY, levelHeight, stops }) => {
              if (stops.length === 0) return null
              const firstX = stops[0].x
              const lastX = stops[stops.length - 1].x
              // The item itself carries on after its last lifecycle or identity
              // event; a cycle, session or relationship is over when it is over.
              const carriesOn = lane.id === 'lifecycle' || lane.id === 'lineage'
              const railEnd = carriesOn ? Math.max(lineEndX, lastX) : lastX
              return (
                <g key={lane.id}>
                  <line x1={firstX} y1={railY} x2={railEnd} y2={railY} strokeWidth={2} style={{ stroke: 'var(--rule-bright)' }} />
                  {stops.map(({ event, x, level }) => (
                    <g key={event.id}>
                      {level > 0 && (
                        <line x1={x} y1={railY + STOP_PX / 2} x2={x} y2={railY + STOP_PX / 2 + 6 + level * levelHeight}
                          style={{ stroke: 'var(--rule-bright)' }} />
                      )}
                      {event.branch === 'in' && (
                        <path fill="none" strokeWidth={2} style={{ stroke: 'var(--slate)' }}
                          d={`M ${x - BRANCH_REACH} ${railY - 30} C ${x - 20} ${railY - 30}, ${x - 26} ${railY}, ${x} ${railY}`} />
                      )}
                      {event.branch === 'out' && (
                        <path fill="none" strokeWidth={2} style={{ stroke: 'var(--slate)' }}
                          d={`M ${x} ${railY} C ${x + 26} ${railY}, ${x + 20} ${railY - 30}, ${x + BRANCH_REACH} ${railY - 30}`} />
                      )}
                    </g>
                  ))}
                </g>
              )
            })}

            {/* Today */}
            <line x1={todayX} y1={RULER_HEIGHT - 6} x2={todayX} y2={totalHeight} style={{ stroke: 'var(--text-pri)' }} />
            <text x={todayX} y={RULER_HEIGHT - 12} textAnchor="middle" className="font-mono" fontSize={12}
              style={{ fill: 'var(--text-pri)', letterSpacing: '0.08em' }}>TODAY</text>

            {playing && (
              <line x1={playheadX} y1={RULER_HEIGHT} x2={playheadX} y2={totalHeight} strokeWidth={2} style={{ stroke: 'var(--mustard)' }} />
            )}
          </svg>

          <StateBand spans={spans} toX={toX} top={RULER_HEIGHT + 16} />

          {laneLayouts.map(({ lane, railY, levelHeight, stops }) => stops.map(({ event, x, level }) => {
            const cfg = EVENT_CONFIG[event.type]
            const Icon = cfg.icon
            const tone = toneFor(event.type)
            const isSelected = event.id === selectedId
            const revealed = playheadProgress === 0 || event.timestamp.getTime() <= playheadT
            const rank = rankOf(event)
            const labelTop = railY + STOP_PX / 2 + 8 + level * levelHeight
            return (
              <div key={event.id} className="transition-opacity duration-300" style={{ opacity: revealed ? 1 : 0.2 }}>
                {/* Filled: it happened on this date. Open: it happened, the date is our best estimate. */}
                <button
                  type="button"
                  aria-label={`${lane.label}: ${event.label}, ${formatDate(event.timestamp)}`}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(isSelected ? null : event.id)}
                  className={`absolute flex items-center justify-center rounded-full transition-transform hover:scale-110 ${FOCUS_RING}`}
                  style={{
                    left: x - STOP_PX / 2, top: railY - STOP_PX / 2, width: STOP_PX, height: STOP_PX,
                    background: event.isEstimated ? 'var(--bg-raised)' : TONE_COLOR[tone],
                    border: `2px solid ${TONE_COLOR[tone]}`,
                    boxShadow: isSelected ? '0 0 0 2px var(--bg-raised), 0 0 0 4px var(--mustard)' : '0 0 0 3px var(--bg-raised)',
                  }}>
                  <Icon style={{ width: 13, height: 13, color: event.isEstimated ? TONE_COLOR[tone] : TONE_INK[tone] }} />
                </button>
                {/* Each line sits on the ground colour so the Today line never runs through a word. */}
                <div className="absolute flex flex-col items-center cursor-pointer" title={event.label}
                  onClick={() => onSelect(isSelected ? null : event.id)}
                  style={{ left: x - LABEL_W_PX / 2, top: labelTop, width: LABEL_W_PX }}>
                  {rank && <span className="px-1 bg-[var(--bg-raised)] font-mono text-sm leading-tight text-[var(--mustard)]">{rank}</span>}
                  <span className={`max-w-full px-1 bg-[var(--bg-raised)] text-xs leading-snug truncate ${isSelected ? 'font-semibold text-[var(--text-pri)]' : 'font-medium text-[var(--text-sec)]'}`}>
                    {event.label}
                  </span>
                  {!rank && event.sublabel && <span className="max-w-full px-1 bg-[var(--bg-raised)] text-xs leading-snug truncate text-[var(--text-ter)]">{event.sublabel}</span>}
                  <span className="px-1 bg-[var(--bg-raised)] font-mono text-xs leading-snug text-[var(--text-dim)]">
                    {event.isEstimated && 'about '}{formatDateShort(event.timestamp)}
                  </span>
                </div>
                {event.branch && event.branchLabel && (() => {
                  const style = event.branch === 'in'
                    ? { right: mapWidth - (x - BRANCH_REACH - 6), top: railY - 39, maxWidth: 190 }
                    : { left: x + BRANCH_REACH + 6, top: railY - 39, maxWidth: 190 }
                  const cls = 'absolute truncate text-xs font-medium text-[var(--text-sec)]'
                  return event.linkedId
                    ? <Link to={`/prioritization/items/${event.linkedId}/journey`} title={`Open the journey of ${event.branchLabel}`}
                        className={`${cls} underline decoration-[var(--rule-bright)] underline-offset-2 hover:text-[var(--text-pri)]`} style={style}>{event.branchLabel}</Link>
                    : <span className={cls} style={style}>{event.branchLabel}</span>
                })()}
              </div>
            )
          }))}
        </div>
      </div>
    </div>
  )
}

// ─── Animation hook ───────────────────────────────────────────────────────────

export const PLAY_DURATION_MS = 5500


export function usePlayhead() {
  const [progress, setProgress] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const rafRef = useRef<number>()
  const startRef = useRef<number>()
  const pausedAt = useRef<number>(0)

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    setIsPlaying(false)
  }, [])

  const play = useCallback(() => {
    if (progress >= 1) {
      setProgress(0)
      pausedAt.current = 0
    }
    setIsPlaying(true)
    startRef.current = undefined
    const animate = (now: number) => {
      if (!startRef.current) startRef.current = now - pausedAt.current * PLAY_DURATION_MS
      const elapsed = now - startRef.current
      const p = Math.min(elapsed / PLAY_DURATION_MS, 1)
      setProgress(p)
      pausedAt.current = p
      if (p < 1) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        setIsPlaying(false)
      }
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [progress])

  const pause = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    setIsPlaying(false)
  }, [])

  const reset = useCallback(() => {
    stop()
    setProgress(0)
    pausedAt.current = 0
  }, [stop])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  return { progress, isPlaying, play, pause, reset }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ItemJourneyPage() {
  const { itemId } = useParams<{ itemId: string }>()
  const navigate = useNavigate()

  const item = useItemsStore((s: any) => s.items.find((i: any) => i.id === itemId))
  const allItems = useItemsStore((s: any) => s.items)
  const { sessions } = useSessionStore()
  const { cycles } = useDegapProcessStore()
  const allGroups = useItemRelationshipsStore((s) => s.groups)
  const allGroupMemberships = useItemRelationshipsStore((s) => s.groupMemberships)
  const allEdges = useItemRelationshipsStore((s) => s.edges)
  const fetchItemAuditTrail = useAuditStore((s) => s.fetchItemAuditTrail)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showEverything, setShowEverything] = useState(false)
  const [zoom, setZoom] = useState(1.0)
  const itemPools = useItemPoolsStore((st) => st.itemPools)
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([])
  const [lineageRows, setLineageRows] = useState<LineageEventRow[]>([])
  const [dataReady, setDataReady] = useState(false)
  // One "now" per visit, so the Today line and the story agree with each other.
  const [now] = useState(() => new Date())

  const { isDesktop } = useVisualViewportFlags()
  const useListLayout = !isDesktop

  const { progress: playheadProgress, isPlaying, play, pause, reset } = usePlayhead()

  useEffect(() => {
    // Non-UUID segments ("/items/intake/journey") can never be a DB item —
    // both fetches below bind the id to uuid columns and would only 22P02.
    if (!isUuid(itemId)) return
    setDataReady(false)
    setSelectedId(null)
    Promise.allSettled([
      fetchItemAuditTrail(itemId).then(setAuditEntries).catch(() => {}),
      getLineageForItem(itemId).then(setLineageRows).catch(() => {}),
    ]).then(() => setDataReady(true))
  }, [itemId, fetchItemAuditTrail])

  const groups = useMemo(() => {
    const ids = new Set(allGroupMemberships.filter((m) => m.itemId === itemId).map((m) => m.groupId))
    return allGroups.filter((g) => ids.has(g.id))
  }, [allGroups, allGroupMemberships, itemId])

  const edges = useMemo(
    () => allEdges.filter((e) => e.sourceItemId === itemId || e.targetItemId === itemId),
    [allEdges, itemId],
  )

  // People a change names (a new owner, a delegate), so it reads as a name.
  const personIds = useMemo(() => namedPeopleIds(auditEntries), [auditEntries])
  const namedPeople = useActorDisplayMap(personIds)

  const allEvents = useMemo(() => {
    if (!item || !dataReady) return []
    return groupChangesByDay(deriveJourneyEvents(item, sessions, cycles, groups, edges, allItems, auditEntries, lineageRows, itemPools, namedPeople))
  }, [item, dataReady, sessions, cycles, groups, edges, allItems, auditEntries, lineageRows, itemPools, namedPeople])

  // Canon (Lineage): the default view is the high-signal events; the full view is on request.
  const keyEvents = useMemo(() => allEvents.filter((e) => isKeyMoment(e.type)), [allEvents])
  const backgroundCount = allEvents.length - keyEvents.length
  const events = showEverything || keyEvents.length === 0 ? allEvents : keyEvents

  // Only render lanes that have at least one event
  const activeLanes = useMemo(
    () => LANES.filter((lane) => events.some((e) => e.lane === lane.id)),
    [events],
  )

  const story = useMemo(() => journeyStory(allEvents, now), [allEvents, now])

  const actorIds = useMemo(() => events.flatMap((e) => [e.actor ?? null, ...(e.changes ?? []).map((c) => c.actor ?? null)]), [events])
  const actorDisplayMap = useActorDisplayMap(actorIds)

  const selectedEvent = events.find((e) => e.id === selectedId)

  if (!item) {
    return (
      <div className="flex items-center justify-center h-64 text-[var(--text-ter)]">
        <p className="text-sm">Item not found.</p>
      </div>
    )
  }

  const pressed = 'bg-[var(--active-tint)] text-[var(--text-pri)]'

  return (
    <div className="flex flex-col min-h-full bg-[var(--bg-raised)]">
      {/* ── Header ── */}
      <header className="flex-shrink-0 border-b px-4 sm:px-6 py-4" style={{ borderColor: 'var(--rule)' }}>
        <div className="flex items-start gap-4 flex-wrap">
          <button
            onClick={() => navigate(`/prioritization/items/${itemId}?tab=lineage`)}
            aria-label="Back to item"
            title="Back to item"
            {...btnSecondary('px-2.5')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="flex-1 min-w-[16rem]">
            <div className={LABEL_MONO}>Item journey</div>
            <h1 className="font-heading font-semibold text-xl leading-tight mt-1" style={{ color: 'var(--text-pri)' }}>{item.title}</h1>
            {story && <p className="font-serif text-[17px] leading-snug mt-1.5 text-[var(--text-sec)] max-w-3xl">{story}</p>}
          </div>

          <div className="flex-shrink-0 flex items-center gap-3 flex-wrap">
            {backgroundCount > 0 && keyEvents.length > 0 && (
              <div className="flex">
                <button aria-pressed={!showEverything} onClick={() => setShowEverything(false)}
                  {...btnSecondary(!showEverything ? pressed : '')}>
                  Key moments
                </button>
                <button aria-pressed={showEverything} onClick={() => setShowEverything(true)}
                  {...btnSecondary(`-ml-px ${showEverything ? pressed : ''}`)}>
                  Everything · {allEvents.length}
                </button>
              </div>
            )}

            {!useListLayout && allEvents.length > 1 && (
              <div className="flex">
                <button onClick={isPlaying ? pause : play} aria-label={isPlaying ? 'Pause' : 'Play the journey'}
                  title={isPlaying ? 'Pause' : 'Play the journey'} {...btnSecondary('px-2.5')}>
                  {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                </button>
                {playheadProgress > 0 && (
                  <button onClick={reset} aria-label="Back to the start" title="Back to the start" {...btnSecondary('px-2.5 -ml-px')}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}

            {!useListLayout && (
              <label className="flex items-center gap-2">
                <span className={LABEL_MONO}>Zoom</span>
                <input
                  type="range" min={1} max={4} step={0.25} value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-24" style={{ accentColor: 'var(--slate)' }}
                />
              </label>
            )}
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">
        {!(useListLayout && selectedEvent) && (
          <div className="flex-1 min-w-0">
            {!dataReady ? (
              <div className="flex items-center justify-center py-24 text-[var(--text-ter)]">
                <div className="text-center">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-30 animate-pulse" />
                  <p className="text-sm">Loading journey…</p>
                </div>
              </div>
            ) : allEvents.length === 0 ? (
              <div className="flex items-center justify-center py-24 text-[var(--text-ter)]">
                <p className="text-sm">Nothing has happened to this item yet.</p>
              </div>
            ) : useListLayout ? (
              <div>
                {activeLanes.map((lane) => (
                  <div key={lane.id}>
                    <div className={`${LABEL_MONO} px-4 py-2.5 bg-[var(--surface)] sticky top-0 z-10 border-b`}
                      style={{ borderColor: 'var(--rule)' }}>
                      {lane.label}
                    </div>
                    {events.filter((e) => e.lane === lane.id).map((event) => (
                      <MobileEventRow key={event.id} event={event} onSelect={setSelectedId} isSelected={event.id === selectedId} />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <JourneyMap
                events={events}
                allEvents={allEvents}
                activeLanes={activeLanes}
                now={now}
                selectedId={selectedId}
                onSelect={setSelectedId}
                playheadProgress={playheadProgress}
                zoom={zoom}
              />
            )}
          </div>
        )}

        {/* ── Detail panel — full width on phone/tablet when open; rail on desktop ── */}
        {selectedEvent && (
          <div
            className={useListLayout ? 'flex-1 min-w-0' : 'flex-shrink-0 border-l'}
            style={useListLayout ? undefined : { width: 300, borderColor: 'var(--rule)' }}
          >
            <DetailPanel
              event={selectedEvent}
              actorDisplayMap={actorDisplayMap}
              onClose={() => setSelectedId(null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
